import React, { createContext, useCallback, useMemo, useState } from 'react';
import { AlertCircle, CheckCircle, Info, AlertTriangle, X } from 'lucide-react';

export const NotificationContext = createContext();

const notificationTypes = {
  error: { icon: AlertCircle, bgColor: 'bg-red-500/10', textColor: 'text-red-400', borderColor: 'border-red-500/30' },
  success: { icon: CheckCircle, bgColor: 'bg-green-500/10', textColor: 'text-green-400', borderColor: 'border-green-500/30' },
  info: { icon: Info, bgColor: 'bg-blue-500/10', textColor: 'text-blue-400', borderColor: 'border-blue-500/30' },
  warning: { icon: AlertTriangle, bgColor: 'bg-yellow-500/10', textColor: 'text-yellow-400', borderColor: 'border-yellow-500/30' }
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);

  const addNotification = useCallback((message, type = 'info', duration = 5000) => {
    const id = Date.now();
    const notification = { id, message, type };

    setNotifications((prev) => [...prev, notification]);

    if (duration > 0) {
      setTimeout(() => {
        setNotifications((prev) => prev.filter((n) => n.id !== id));
      }, duration);
    }

    return id;
  }, []);

  const removeNotification = useCallback((id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const showError = useCallback((message, duration = 5000) => addNotification(message, 'error', duration), [addNotification]);
  const showSuccess = useCallback((message, duration = 3000) => addNotification(message, 'success', duration), [addNotification]);
  const showInfo = useCallback((message, duration = 4000) => addNotification(message, 'info', duration), [addNotification]);
  const showWarning = useCallback((message, duration = 5000) => addNotification(message, 'warning', duration), [addNotification]);
  const value = useMemo(() => ({
    addNotification,
    removeNotification,
    showError,
    showSuccess,
    showInfo,
    showWarning,
    notifications
  }), [addNotification, notifications, removeNotification, showError, showInfo, showSuccess, showWarning]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <NotificationContainer notifications={notifications} onRemove={removeNotification} />
    </NotificationContext.Provider>
  );
};

const NotificationContainer = ({ notifications, onRemove }) => {
  return (
    <div className="fixed bottom-20 left-4 right-4 z-[100] space-y-2 sm:bottom-auto sm:left-auto sm:top-20 sm:max-w-md" aria-live="polite" aria-atomic="false">
      {notifications.map((notification) => (
        <NotificationItem
          key={notification.id}
          notification={notification}
          onRemove={() => onRemove(notification.id)}
        />
      ))}
    </div>
  );
};

const NotificationItem = ({ notification, onRemove }) => {
  const typeConfig = notificationTypes[notification.type] || notificationTypes.info;
  const IconComponent = typeConfig.icon;

  return (
    <div
      role="status"
      className={`${typeConfig.bgColor} ${typeConfig.borderColor} border rounded-[6px] bg-[#0d1a2b] p-4 flex items-start gap-3 shadow-xl`}
    >
      <IconComponent className={`${typeConfig.textColor} w-5 h-5 flex-shrink-0 mt-0.5`} />
      <p className={`${typeConfig.textColor} text-sm flex-1`}>{notification.message}</p>
      <button
        onClick={onRemove}
        className={`${typeConfig.textColor} -m-3 ml-0 flex h-11 w-11 flex-shrink-0 items-center justify-center hover:opacity-75`}
        aria-label="Dismiss notification"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export const useNotification = () => {
  const context = React.useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within NotificationProvider');
  }
  return context;
};
